/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectorRef, Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {StateService, UIRouter} from '@uirouter/core';
import * as _ from 'lodash';

import template from './search-page.component.html';
import {ResourceHelper} from '../../components/util/resource-helper';
import {ObjectTypeFinder} from '../common/constants/object-type';
import {objectTypeStateParams} from '../common/filters/object-type-state-params';
import {SearchCoordinatorService} from './search-coordinator.service';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {BrowserLocationService} from '../angular-platform-providers';
import {AppEventBusService} from '../common/events/app-event-bus.service';
import {createBufferedFlag, readBufferedFlag} from '../common/util/template-state';

const LABEL_CAPS = ['LABEL', 'RATE_CONTROL_LABELS', 'EXCLUSION_LABEL', 'MATCHING_LABEL'];

@Component({
  standalone: false,
  selector: 'adn-search-page',
  template
})
export class SearchPageComponent implements OnInit, OnDestroy {
  term: string;
  labels: string;
  rateControlLabels: string;
  aggregations: any[] = [];
  includeDeleted = false;
  isPureMarketplace = false;
  syncing = false;
  searchTerm: string;
  searchResults: any = {items: []};
  searchMeta: any;

  /**
   * Fast / cached search can settle during the first CD turn.
   * *ngIf="syncing" / zero-hits would then flip true→false mid-check (NG0100).
   */
  private readonly syncingFlag = createBufferedFlag();
  private readonly zeroHitsFlag = createBufferedFlag();
  private readonly hasHitsFlag = createBufferedFlag();
  private destroyed = false;
  private destroyLocationListener: () => void;

  constructor(
    private readonly stateService: StateService,
    @Inject(BrowserLocationService) private readonly location: any,
    private readonly translate: TranslateService,
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    private readonly searchCoordinator: SearchCoordinatorService,
    private readonly uiRouter: UIRouter,
    private readonly eventBus: AppEventBusService,
    private readonly cdr: ChangeDetectorRef
  ) {
  }

  get displaySyncing(): boolean {
    return readBufferedFlag(this.syncingFlag, this.syncing, this.cdr, () => !this.destroyed);
  }

  get hasZeroHits(): boolean {
    return readBufferedFlag(this.zeroHitsFlag, this.searchResults?.totalHits === 0, this.cdr, () => !this.destroyed);
  }

  get hasHits(): boolean {
    return readBufferedFlag(this.hasHitsFlag, !!this.searchResults?.totalHits, this.cdr, () => !this.destroyed);
  }

  ngOnInit(): void {
    const routeParams = this.uiRouter.globals.params;
    this.term = ResourceHelper.uiSrefUpdater(routeParams.q);
    this.labels = ResourceHelper.uiSrefUpdater(routeParams.labels);
    this.rateControlLabels = routeParams.rateControlLabels;
    this.aggregations = this.searchCoordinator.aggregations;
    this.includeDeleted = _.get(routeParams, 'objectState', '').indexOf('HIDDEN') > -1;
    this.isPureMarketplace = this.LocalNetworkProfile.isPureMarketplacePlatform();
    this.searchResults = this.searchCoordinator.searchResults;

    this.searchMeta = {
      pageSize: 50,
      changePage: () => {
        this.toggleFilter('page', this.searchMeta.currentPage);
        this.syncSearch();
      },
      resolved: true
    };

    this.syncSearch();

    this.destroyLocationListener = this.eventBus.on('$locationChangeSuccess', this.syncSearch);
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    if (this.destroyLocationListener) {
      this.destroyLocationListener();
    }
  }

  submit(term: string): void {
    this.stateService.go(this.stateService.current, {q: term}, {inherit: false});
  }

  labelSearch(label: any): void {
    this.stateService.go(this.stateService.current, objectTypeStateParams(label), {inherit: false});
  }

  includeDeletedChange(): void {
    const currentSearch = this.location.queryParams;
    currentSearch.objectState = this.includeDeleted ? 'HIDDEN;ACTIVE;INACTIVE' : 'ACTIVE;INACTIVE';
    this.location.setQueryParams(currentSearch);
    this.syncSearch();
  }

  toggleFilter(param: string, value?: any): void {
    const currentSearch = this.location.queryParams;
    if (param !== 'page') {
      delete currentSearch.page;
    }
    if (currentSearch[param] && (param !== 'page' || value < 2)) {
      delete currentSearch[param];
      this.location.setQueryParams(currentSearch);
      this.syncSearch();
      return;
    }
    this.location.setQueryParam(param, value);
    this.syncSearch();
  }

  getFilterValue(aggregation: any): string {
    const filter = this.location.queryParams[aggregation.name];
    return _.get(_.find(aggregation.buckets, ['filter', filter]), 'name', filter);
  }

  appliedAggregations(): any[] {
    return this.filterAggregations(false);
  }

  availableAggregations(): any[] {
    return this.filterAggregations(true);
  }

  aggregationHeading(aggregationName: string): string {
    const key = 'search.aggregation.' + aggregationName;
    const translated = this.translate.instant(key);
    if (_.isString(translated) && translated !== key) {
      return translated;
    }

    const nestedKey = key + '.' + aggregationName;
    const nestedTranslation = this.translate.instant(nestedKey);
    return _.isString(nestedTranslation) && nestedTranslation !== nestedKey
      ? nestedTranslation
      : _.startCase(aggregationName);
  }

  aggregationLabel(aggregationName: string, value: string, isEarningsAccount = false): string {
    const marketplaceSuffix = isEarningsAccount && this.isPureMarketplace ? '.marketplace' : '';
    const key = 'search.aggregation' + marketplaceSuffix + '.' + aggregationName + '.' + value;
    const translated = this.translate.instant(key);
    if (_.isString(translated) && translated !== key) {
      return translated;
    }
    if (aggregationName === 'types') {
      const objectType = ObjectTypeFinder.findSearchObject(value);
      if (objectType?.name) {
        return objectType.name;
      }
    }
    return value;
  }

  isStandardResult(result: any): boolean {
    return LABEL_CAPS.indexOf(result.objectType.caps) === -1 && result.objectType.caps !== 'USER_SEGMENT';
  }

  isLabelResult(result: any): boolean {
    return LABEL_CAPS.indexOf(result.objectType.caps) > -1;
  }

  isUserSegmentResult(result: any): boolean {
    return result.objectType.caps === 'USER_SEGMENT';
  }

  getResultHref(result: any): string {
    if (!result?.objectType?.stateName) {
      return '#';
    }
    return this.stateService.href(result.objectType.stateName, objectTypeStateParams(result));
  }

  private filterAggregations(inverse: boolean): any[] {
    const filters = this.location.queryParams;
    return _.filter(this.aggregations, (aggregation) => {
      const isFiltered = filters[aggregation.name];
      return inverse ? !isFiltered : isFiltered;
    });
  }

  private syncSearch = (): void => {
    if (this.syncing) {
      return;
    }
    this.syncing = true;
    const params = this.location.queryParams;
    this.searchMeta.currentPage = params.page || 1;
    this.searchCoordinator.sync(params, this.searchMeta).then(() => {
      this.syncing = false;
      this.term = params.q;
      this.searchTerm = params.q;
      this.labels = params.labels;
    }).catch(() => {
      this.syncing = false;
    });
  };
}
